<script setup lang="ts">
import ExclamationTriangle from '@primeicons/vue/exclamation-triangle';
import Card from 'primevue/card';
import type { ShipmentExceptionRow } from '@gpx-spa/queries/src/shipment-exception-types';
import {
  displayExceptionEventTitle,
  exceptionSeverityLabel,
} from '@/utils/shipment-exception-display';

defineProps<{
  exceptions: ShipmentExceptionRow[];
}>();

const emit = defineEmits<{
  viewExceptions: [];
}>();

function severityDot(severity: string): string {
  const s = severity.toLowerCase();
  if (s === 'critical' || s === 'high') return 'bg-red-500';
  if (s === 'warning' || s === 'medium') return 'bg-amber-500';
  return 'bg-sky-500';
}

const cardPt = {
  root: {
    class:
      '!rounded-lg !border !border-neutral-200 !bg-white !shadow-none overflow-hidden',
  },
  body: { class: '!p-0' },
  content: { class: '!p-0' },
};
</script>

<template>
  <Card
    v-if="exceptions.length > 0"
    :pt="cardPt"
    class="cursor-pointer transition-shadow hover:shadow-sm"
    @click="emit('viewExceptions')"
  >
    <template #content>
      <div class="border border-amber-200 bg-amber-50 p-4 text-amber-900">
        <div class="flex items-start gap-3">
          <ExclamationTriangle
            class="mt-0.5 shrink-0 text-amber-600"
            aria-hidden="true"
          />
          <div class="min-w-0 flex-1">
            <h4 class="text-sm font-semibold">
              {{ exceptions.length }} Open Exception{{
                exceptions.length !== 1 ? 's' : ''
              }}
            </h4>
            <div class="mt-2 space-y-1.5">
              <div
                v-for="row in exceptions.slice(0, 3)"
                :key="row.id"
                class="flex items-center gap-2 text-xs"
              >
                <span
                  class="size-1.5 shrink-0 rounded-full"
                  :class="severityDot(row.severity)"
                />
                <span class="font-medium">{{
                  exceptionSeverityLabel(row.severity)
                }}</span>
                <span class="opacity-70">{{
                  displayExceptionEventTitle(row.type)
                }}</span>
              </div>
              <p v-if="exceptions.length > 3" class="text-xs opacity-70">
                + {{ exceptions.length - 3 }} more
              </p>
            </div>
            <p class="mt-2 text-xs opacity-70">Click to view all exceptions</p>
          </div>
        </div>
      </div>
    </template>
  </Card>
</template>
